import { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Save, Loader2, Megaphone } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const languages = [
  { code: 'ca', name: 'Català', flag: '🇦🇩' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

const AdminTicker = () => {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [text, setText] = useState({ ca: '', es: '', fr: '', en: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/admin/ticker`);
        if (res.ok) {
          const data = await res.json();
          setEnabled(data.enabled !== false);
          setText({ ca: '', es: '', fr: '', en: '', ...(data.text || {}) });
        }
      } catch (_) {
        toast.error('Error carregant el ticker');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authFetch]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/ticker`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled, text }),
      });
      if (res.ok) {
        toast.success('Avís guardat correctament');
      } else {
        toast.error('Error guardant (sessió caducada?)');
      }
    } catch (_) {
      toast.error('Error guardant el ticker');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-3xl">
        <div className="flex items-center gap-3 mb-2">
          <Megaphone className="w-7 h-7 text-[#1A4D2E]" />
          <h1 className="text-2xl font-bold text-[#1A4D2E]">Avís al peu (Ticker)</h1>
        </div>
        <p className="text-gray-500 mb-6 text-sm">
          Text en moviment que apareix a la part inferior de totes les pàgines públiques de la web.
        </p>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between bg-white border rounded-lg p-4">
              <div>
                <Label className="font-semibold">Ticker actiu</Label>
                <p className="text-xs text-gray-500 mt-1">
                  Desactiva'l quan no hi hagi avisos per mostrar.
                </p>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                data-testid="ticker-enabled-switch"
              />
            </div>

            {languages.map((l) => (
              <div key={l.code} className="bg-white border rounded-lg p-4">
                <Label className="font-semibold flex items-center gap-2 mb-2">
                  <span>{l.flag}</span> {l.name}
                </Label>
                <Textarea
                  rows={2}
                  value={text[l.code] || ''}
                  onChange={(e) => setText((prev) => ({ ...prev, [l.code]: e.target.value }))}
                  placeholder={`Text de l'avís en ${l.name}...`}
                  data-testid={`ticker-text-${l.code}`}
                />
              </div>
            ))}

            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#1A4D2E] hover:bg-[#143d24] text-white"
              data-testid="ticker-save-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTicker;
